import { config } from "@/app/config";
import { supabase } from "@/api/supabase";
import { errorMessage } from "@/lib/toast";
import { z } from "zod";

export class MiddlewareError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Parsed JSON error body (FastAPI's `{ detail }`), or the raw text when it isn't JSON. */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "MiddlewareError";
  }
}

/**
 * Typed fetch wrapper for the REST middleware at `VITE_API_BASE_URL`
 * (fronts the EFS SOAP Card Management API). Every call carries the current
 * Supabase access token as a bearer token — the middleware validates the
 * Supabase JWT itself, there is no separate login.
 *
 * Matches the Flutter app's contract exactly: `API_BASE_URL` + path (no
 * leading slash — the base URL must end with a trailing slash, enforced in
 * `app/config.ts`), non-200 → throw.
 */
async function middlewareFetch<TResponse>(
  path: string,
  init?: RequestInit,
): Promise<TResponse> {
  if (readOnly && !path.startsWith("platform/")) {
    throw new MiddlewareError("Support mode is read-only", 403, { detail: "Support mode is read-only." });
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      ...init?.headers,
    },
  });

  // Some middleware endpoints (report regeneration) return no body.
  const text = await response.text();

  if (!response.ok) {
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : undefined;
    } catch {
      // Non-JSON error body (proxy/HTML error page) — keep the raw text.
    }
    throw new MiddlewareError(
      `Middleware request to "${path}" failed with HTTP ${response.status}`,
      response.status,
      body,
    );
  }

  return (text ? JSON.parse(text) : undefined) as TResponse;
}

let readOnly = false;

/**
 * In support mode the browser must not change anything. The platform admin's token carries no
 * organization, so the middleware would refuse these calls anyway; this stops them before they are sent
 * and gives a clear message. Platform console calls (`platform/…`) are unaffected.
 */
export function setMiddlewareReadOnly(value: boolean): void {
  readOnly = value;
}

export const middleware = {
  get: <TResponse>(path: string) => middlewareFetch<TResponse>(path, { method: "GET" }),
  delete: <TResponse>(path: string) => middlewareFetch<TResponse>(path, { method: "DELETE" }),
  post: <TResponse>(path: string, body: unknown) =>
    middlewareFetch<TResponse>(path, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patch: <TResponse>(path: string, body: unknown) =>
    middlewareFetch<TResponse>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** Upload a file as the raw request body (its own type as Content-Type). */
  putFile: <TResponse>(path: string, file: Blob) =>
    middlewareFetch<TResponse>(path, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    }),
  put: <TResponse>(path: string, body: unknown) =>
    middlewareFetch<TResponse>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

const errorBodySchema = z.object({
  detail: z.union([
    z.string(),
    z.object({ message: z.string() }),
    // FastAPI's 422 validation errors.
    z.array(z.object({ msg: z.string() })).min(1),
  ]),
});

/** The middleware's own explanation (`detail` string, `detail.message`, or the first validation `msg`), falling back to the generic error text. */
export function middlewareErrorMessage(error: unknown): string {
  if (error instanceof MiddlewareError) {
    const parsed = errorBodySchema.safeParse(error.body);
    if (parsed.success) {
      const { detail } = parsed.data;
      if (typeof detail === "string") {
        return detail;
      }
      // pydantic prefixes custom validator messages with "Value error, "
      return Array.isArray(detail)
        ? (detail[0]?.msg.replace(/^Value error, /, "") ?? errorMessage(error))
        : detail.message;
    }
  }
  return errorMessage(error);
}
